import type { MetaFunction } from 'react-router'
import { STATIC_PAGE_HEADERS } from "~/lib/cache.server"
import { Link } from 'react-router'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { HowToSchema } from '~/components/seo-content'
import { FAQSchema, BreadcrumbSchema } from '~/components/structured-data'
import { FileText, Users, CheckCircle, Star, Shield, Clock, ArrowRight, Zap } from 'lucide-react'

const HOW_TO_STEPS = [
  {
    step: '1',
    title: 'Décrivez votre projet',
    detail: "Remplissez le formulaire en 2 minutes : type de travaux, localisation, urgence et budget estimé. Plus c'est précis, plus les devis seront adaptés.",
  },
  {
    step: '2',
    title: 'Recevez 3 devis gratuits',
    detail: "AfficheServices transmet votre demande à 3 artisans vérifiés de votre zone. Vous recevez les devis sous 48 heures, sans engagement.",
  },
  {
    step: '3',
    title: "Choisissez votre artisan",
    detail: "Comparez les devis, consultez les avis clients, vérifiez les certifications (RGE, Qualibat) et choisissez le professionnel qui vous convient. AfficheServices reste votre médiateur en cas de problème.",
  },
]

const FAQS = [
  { question: "Le service est-il vraiment gratuit pour les particuliers ?", answer: "Oui, 100 % gratuit et sans engagement. AfficheServices ne facture jamais les particuliers ; notre modèle économique repose sur les abonnements et les leads achetés par les artisans." },
  { question: "Combien de temps pour recevoir les devis ?", answer: "La plupart des devis arrivent sous 24 à 48 heures. Pour les urgences (fuite, panne électrique, serrure bloquée), les artisans peuvent répondre sous 1 à 4 heures." },
  { question: "Comment sont vérifiés les artisans ?", answer: "Chaque artisan passe par 3 contrôles : vérification SIRET via la base SIRENE de l'INSEE, contrôle de l'assurance responsabilité civile professionnelle, et vérification d'identité (KYC) pour les comptes Pro et Enterprise." },
  { question: "Que faire en cas de problème avec un artisan ?", answer: "AfficheServices propose une médiation gratuite en cas de litige. Notre équipe contacte l'artisan pour trouver une solution amiable. Si le litige persiste, nous orientons vers les organismes compétents (Médiateur de la consommation, DGCCRF)." },
  { question: "Les devis m'engagent-ils à signer ?", answer: "Non. Un devis est une proposition — il ne vous engage que lorsque vous le signez. Vous pouvez refuser tous les devis sans justification ni frais." },
]

export function headers() { return STATIC_PAGE_HEADERS }

export const meta: MetaFunction = () => [
  { title: 'Comment ça marche — 3 devis gratuits sous 48h | AfficheServices' },
  { name: 'description', content: 'AfficheServices en 3 étapes : décrivez votre projet en 2 min, recevez 3 devis comparatifs d\'artisans SIRET-vérifiés sous 48h, choisissez le meilleur. 100 % gratuit, sans engagement.' },
  { tagName: 'link', rel: 'canonical', href: 'https://www.afficheservices.fr/comment-ca-marche' },
  { property: 'og:image', content: 'https://www.afficheservices.fr/api/og?title=Comment+ca+marche&subtitle=3+etapes+simples' },
]

export default function CommentCaMarche() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <HowToSchema name="Comment trouver un artisan vérifié sur AfficheServices" steps={HOW_TO_STEPS} />
      <FAQSchema items={FAQS} />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Comment ça marche', url: 'https://www.afficheservices.fr/comment-ca-marche' },
      ]} />
      <div className="mx-auto max-w-4xl px-4 py-16 lg:px-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-slate-900">Comment ça marche ?</h1>
          <p className="mt-3 text-lg text-slate-500">
            Comparez 3 devis d'artisans <strong>SIRET-vérifiés et assurés RC Pro</strong> en 3 étapes.
            Réponse sous 48 heures, 100 % gratuit, sans inscription ni engagement.
          </p>
        </div>

        {/* Steps */}
        <div className="mt-16 space-y-16">
          {/* Step 1 */}
          <div className="grid gap-8 lg:grid-cols-2 lg:items-center">
            <div>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">1</div>
                <h2 className="text-xl font-bold text-slate-900">Décrivez votre projet</h2>
              </div>
              <p className="mt-4 text-sm text-slate-500 leading-relaxed">
                Remplissez notre formulaire en 2 minutes. Indiquez le type de travaux, votre localisation,
                le niveau d'urgence et votre budget estimé. Plus vous êtes précis, plus les devis seront adaptés.
              </p>
              <ul className="mt-4 space-y-2">
                {['20+ catégories de services', 'Quiz guidé étape par étape', 'Estimation de prix instantanée'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle className="h-4 w-4 text-emerald-500" /> {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex items-center justify-center rounded-2xl bg-blue-50 p-8">
              <FileText className="h-24 w-24 text-blue-300" />
            </div>
          </div>

          {/* Step 2 */}
          <div className="grid gap-8 lg:grid-cols-2 lg:items-center">
            <div className="order-2 flex items-center justify-center rounded-2xl bg-amber-50 p-8 lg:order-1">
              <Users className="h-24 w-24 text-amber-300" />
            </div>
            <div className="order-1 lg:order-2">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-sm font-bold text-amber-600">2</div>
                <h2 className="text-xl font-bold text-slate-900">Recevez jusqu'à 3 devis</h2>
              </div>
              <p className="mt-4 text-sm text-slate-500 leading-relaxed">
                Nous envoyons votre demande aux artisans vérifiés les plus qualifiés dans votre zone.
                Ils vous contactent directement sous 48h avec leurs devis personnalisés.
              </p>
              <ul className="mt-4 space-y-2">
                {['Maximum 3 artisans contactés', 'Tous vérifiés (SIRET + assurance)', 'Devis gratuits et sans engagement'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle className="h-4 w-4 text-emerald-500" /> {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Step 3 */}
          <div className="grid gap-8 lg:grid-cols-2 lg:items-center">
            <div>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-sm font-bold text-emerald-600">3</div>
                <h2 className="text-xl font-bold text-slate-900">Comparez et choisissez</h2>
              </div>
              <p className="mt-4 text-sm text-slate-500 leading-relaxed">
                Comparez les devis, consultez les profils, les avis clients et les portfolios.
                Choisissez le professionnel qui vous convient le mieux. Aucune obligation.
              </p>
              <ul className="mt-4 space-y-2">
                {['Comparaison transparente des prix', 'Avis clients vérifiés', 'Profils détaillés avec certifications'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle className="h-4 w-4 text-emerald-500" /> {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex items-center justify-center rounded-2xl bg-emerald-50 p-8">
              <Star className="h-24 w-24 text-emerald-300" />
            </div>
          </div>
        </div>

        {/* Trust badges */}
        <div className="mt-16 grid grid-cols-2 gap-4 sm:grid-cols-4">
          {[
            { icon: Shield, value: '100%', label: 'Artisans vérifiés', color: 'text-blue-600' },
            { icon: Clock, value: '48h', label: 'Délai de réponse', color: 'text-amber-600' },
            { icon: Zap, value: 'Gratuit', label: 'Pour les particuliers', color: 'text-emerald-600' },
            { icon: Star, value: '3 max', label: 'Devis par demande', color: 'text-violet-600' },
          ].map((s) => (
            <div key={s.label} className="rounded-xl border border-slate-100 bg-slate-50 p-5 text-center">
              <s.icon className={`mx-auto h-6 w-6 ${s.color}`} />
              <p className="mt-2 text-xl font-bold text-slate-900">{s.value}</p>
              <p className="mt-1 text-xs text-slate-500">{s.label}</p>
            </div>
          ))}
        </div>

        {/* FAQ */}
        <div className="mt-16 border-t border-slate-100 pt-10">
          <h2 className="text-xl font-bold text-slate-900">Questions fréquentes</h2>
          <div className="mt-6 space-y-4">
            {[
              { q: 'Est-ce vraiment gratuit ?', a: 'Oui, le service est 100% gratuit pour les particuliers. Vous recevez des devis sans aucun frais. Seuls les professionnels paient pour recevoir des leads.' },
              { q: 'Combien de temps pour recevoir des devis ?', a: 'En moyenne, vous recevez vos premiers devis sous 48 heures. Pour les demandes urgentes, les professionnels peuvent vous contacter en quelques heures.' },
              { q: 'Suis-je obligé d\'accepter un devis ?', a: 'Non, vous êtes libre de comparer et de choisir. Il n\'y a aucune obligation ni engagement. Vous pouvez refuser tous les devis sans justification.' },
              { q: 'Comment les artisans sont-ils vérifiés ?', a: 'Chaque artisan est vérifié via le registre SIRENE (SIRET), son assurance RC Pro est contrôlée, et son identité est confirmée. Consultez notre charte qualité pour en savoir plus.' },
            ].map((faq) => (
              <details key={faq.q} className="group rounded-xl border border-slate-100 bg-white">
                <summary className="flex cursor-pointer items-center justify-between p-4 text-sm font-semibold text-slate-900 hover:text-primary">
                  {faq.q}
                  <span className="shrink-0 text-slate-300 transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="px-4 pb-4 text-sm text-slate-500 leading-relaxed">{faq.a}</p>
              </details>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 rounded-2xl bg-gradient-to-r from-primary to-blue-600 p-8 text-center text-white sm:p-12">
          <h2 className="text-2xl font-bold">Prêt à trouver votre artisan ?</h2>
          <p className="mt-2 text-blue-100">Décrivez votre projet en 2 minutes et recevez 3 devis gratuits.</p>
          <Link to="/demande-devis" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-white px-8 py-3.5 text-sm font-semibold text-primary shadow-md hover:bg-slate-50 btn-press">
            Commencer maintenant <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
      {/* Trust & transparency */}
      <section className="border-t border-slate-100 bg-surface-secondary py-14">
        <div className="mx-auto max-w-3xl px-4 lg:px-6">
          <h2 className="text-xl font-bold text-slate-900">Nos engagements qualité</h2>
          <div className="mt-6 space-y-3 text-sm leading-relaxed text-slate-600">
            <p><strong>Vérification systématique :</strong> Chaque artisan est contrôlé avant d'intégrer notre réseau — SIRET actif (vérification INSEE), assurance RC Pro en cours de validité, garantie décennale pour les métiers concernés, et identité du gérant confirmée.</p>
            <p><strong>Transparence des prix :</strong> Les devis envoyés par nos artisans doivent détailler la main d'œuvre et les fournitures séparément, conformément à l'article L.111-1 du Code de la consommation. Aucun frais caché n'est toléré.</p>
            <p><strong>Suivi qualité :</strong> Après chaque intervention, nous invitons le client à laisser un avis vérifié. Les artisans dont la note passe en dessous de 3/5 sont automatiquement suspendus. Ce mécanisme garantit un niveau de service constamment élevé.</p>
            <p><strong>Médiation :</strong> En cas de litige, notre équipe intervient comme médiateur entre le client et l'artisan. Nous pouvons suspendre un compte professionnel et vous orienter vers la DGCCRF si nécessaire.</p>
          </div>
          <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Sources réglementaires</p>
            <ul className="mt-2 space-y-1 text-xs text-slate-500">
              <li>• <a href="https://www.service-public.fr/particuliers/vosdroits/F31325" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Service-Public.fr</a> — Obligations des artisans du bâtiment</li>
              <li>• <a href="https://www.economie.gouv.fr/dgccrf" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">DGCCRF</a> — Protection des consommateurs</li>
            </ul>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}
